/**
 * openEventDiscussion — opens the community-chat discussion thread for an event
 * (live / quick-room / offline event) from anywhere an event card is shown
 * (home calendar, calendar page, master feed, «События» tab).
 *
 * Event cards carry the event id (`refId`) and the master's auth user id
 * (`masterUserId`) — and, for an offline event, sometimes the discussion id
 * itself (`UnifiedEventDto.discussionId`). When they don't, the discussion id
 * is resolved on the click:
 *   1. masterUserId → masterProfileId   (getPublicUserProfile)
 *   2. offline event → its own card     (getEvent — carries `discussionId`)
 *      room / live   → its own card     (getRoomById / getLiveById, CORE-1696)
 *   3. masterProfileId → discussions    (getMasterChatDiscussions)
 *   4. find discussion where linkedEntityId === refId (and type matches)
 *
 * Then on web we deep-link to the unified profile chat tab
 * (`/dashboard?viewedUserId=…#communityChat/<discussionId>`); on native we push
 * the EventThreadScreen.
 *
 * CORE-1654 (QA 17.10) — a deleted discussion must read as deleted, not as
 * «nothing here, have the general chat». The backend keeps the event and its
 * feed post after `DELETE …/topic` and never recreates the discussion, so the
 * feed card outlives the thread. An offline event tells us on its own card
 * whether it has a discussion (`discussionId`), but «none» alone is not
 * «deleted»: an event published without an announcement, or a private one,
 * never had a thread (dev data, 17.10: 44 of 88 public events, all of them
 * without a feed post). What does imply a thread is the announcement card
 * itself — every public event with a feed post got its discussion at
 * publication. So only a click from an announcement card (`announced`) on a
 * public event with no `discussionId` opens the chat tab in the «Обсуждение
 * удалено» state (`#communityChat/deleted`); the calendar and the «События»
 * tab keep the old general-chat fallback.
 *
 * CORE-1737 — the backend now puts `discussionId` on the feed post of every
 * event announcement (live, room, offline) and on the live / room / event
 * cards, and keeps it after the discussion is deleted. Callers that have it
 * pass it in and skip every lookup: the chat tab tells a live thread from a
 * deleted one by itself (404 `discussion_deleted`). The `announced` rule
 * above stays only as the fallback for a backend without the field.
 *
 * Resolves to `'opened'` once navigation was triggered, `'deleted'` when the
 * discussion is known to be gone (web has navigated to the deleted state;
 * native callers show the notice themselves) and `'unresolved'` when the
 * master could not be resolved at all — the caller must surface that
 * (CORE-1264: a silent no-op on the calendar page was a Production defect).
 */
import { Platform } from 'react-native';
import { api } from '../services/api';
import type { FeedItemKind } from '../types/auth';
import { DELETED_DISCUSSION_ROUTE } from './communityChatLink';

/** Map a feed item kind to the discussion's `linkedEntityType`. */
export function feedKindToLinkedEntityType(kind: FeedItemKind): string | null {
  if (kind.startsWith('live_')) return 'live';
  if (kind.startsWith('room_')) return 'watch_party';
  if (kind.startsWith('offline_') || kind === 'event_saved') return 'master_event';
  return null; // practice_uploaded etc. — not an event with a discussion
}

export type OpenEventDiscussionResult = 'opened' | 'deleted' | 'unresolved';

interface OpenEventDiscussionArgs {
  /**
   * Auth user id of the event's master — becomes `?viewedUserId=`. Optional:
   * if absent it's resolved from masterProfileId. At least one of
   * masterUserId / masterProfileId must be provided.
   */
  masterUserId?: string | null;
  /** Event entity id — matched against `discussion.linkedEntityId`. */
  refId: number;
  /** Expected `discussion.linkedEntityType` (disambiguates id collisions). */
  linkedEntityType?: string | null;
  /** Pass it if the caller already knows it — skips one resolve request. */
  masterProfileId?: number | null;
  /**
   * Discussion id when the card already carries it (`UnifiedEvent.discussionId`
   * from the calendar list). Skips the lookups entirely; the chat tab itself
   * tells a live thread from a deleted one (404 → «Обсуждение удалено»).
   */
  discussionId?: string | null;
  /**
   * The card is the event's published feed announcement (CommunityFeed). A
   * public event with a post always had a discussion, so «no discussion» on
   * such a card means it was deleted — nowhere else can that be told apart
   * from «never had one».
   */
  announced?: boolean;
  masterName?: string | null;
  /** Required for native navigation; unused on web. */
  navigation?: { navigate: (name: string, params?: Record<string, unknown>) => void } | null;
}

/** Build the web deep-link URL (exported for tests / reuse). */
export function buildDiscussionUrl(masterUserId: string, discussionId: string | null): string {
  const base = `/dashboard?viewedUserId=${encodeURIComponent(masterUserId)}`;
  const hash = discussionId ? `#communityChat/${encodeURIComponent(discussionId)}` : '#communityChat';
  return `${base}${hash}`;
}

export async function openEventDiscussion({
  masterUserId,
  refId,
  linkedEntityType,
  masterProfileId,
  discussionId: knownDiscussionId,
  announced = false,
  masterName,
  navigation,
}: OpenEventDiscussionArgs): Promise<OpenEventDiscussionResult> {
  // Calendar mappers use 0 as «no profile id» (CORE-1264) — treat it as unknown
  // so we resolve through masterUserId instead of hitting /masters/0/….
  const knownProfileId = masterProfileId != null && masterProfileId > 0 ? masterProfileId : null;
  if (!masterUserId && knownProfileId == null) return 'unresolved';

  let resolvedUserId = masterUserId ?? null;
  let resolvedProfileId = knownProfileId;

  // Feed items carry a synthetic Stream actor id, not a real auth user id:
  // `master-{profileId}` (owner/master actor) or `user-{userId}`. Unwrap it so
  // the deep-link gets a real `viewedUserId` and the discussion lookup gets the
  // profile id.
  if (resolvedUserId) {
    const masterMatch = /^master-(\d+)$/.exec(resolvedUserId);
    const userMatch = /^user-(.+)$/.exec(resolvedUserId);
    if (masterMatch) {
      if (resolvedProfileId == null) resolvedProfileId = Number(masterMatch[1]);
      resolvedUserId = null; // resolve the real user id below from the profile
    } else if (userMatch) {
      resolvedUserId = userMatch[1];
    }
  }

  // Resolve whichever id we're missing — discussions need the profile id, the
  // web deep-link needs the auth user id.
  if (resolvedProfileId == null && resolvedUserId) {
    try {
      resolvedProfileId = (await api.getPublicUserProfile(resolvedUserId)).masterProfileId;
    } catch {
      resolvedProfileId = null;
    }
  }
  if (resolvedUserId == null && resolvedProfileId != null) {
    try {
      resolvedUserId = (await api.getPublicMasterProfile(resolvedProfileId)).userId ?? null;
    } catch {
      resolvedUserId = null;
    }
  }

  let discussionId: string | null = knownDiscussionId ?? null;
  // The discussion is known to be deleted: the card is a published
  // announcement of a public event, and the event itself says it has none.
  let deleted = false;

  if (!discussionId && linkedEntityType === 'master_event') {
    try {
      const event = await api.getEvent('master', refId);
      discussionId = event.discussionId ?? null;
      // A private event never gets a discussion (verified on dev) — that is
      // «no thread», not «deleted».
      if (!discussionId && announced && !event.isPrivate) deleted = true;
    } catch {
      // Event unreadable (deleted, private to someone else) — fall through to
      // the list lookup; it tells «no thread» but cannot tell «deleted».
    }
  }

  // CORE-1696 (QA 17.10) — the discussions list answers 403
  // `subscription_required` to a guest and to anyone not subscribed, so a
  // room or live card without `discussionId` lost the thread right here and
  // the guest landed on the general chat; after signing in there was nothing
  // to return to. The room and live cards themselves are public and carry
  // `discussionId` (CORE-1737), so they are read first — one request, and it
  // works for everyone; the list stays as the fallback for an older backend.
  if (!discussionId && !deleted && linkedEntityType === 'watch_party') {
    try {
      discussionId = (await api.getRoomById(refId)).discussionId ?? null;
    } catch {
      // Private or gone — the list below still gets a chance.
    }
  }
  if (!discussionId && !deleted && linkedEntityType === 'live') {
    try {
      discussionId = (await api.getLiveById(refId)).discussionId ?? null;
    } catch {
      // Same as above.
    }
  }

  if (!discussionId && !deleted && resolvedProfileId != null) {
    try {
      const discussions = await api.getMasterChatDiscussions(resolvedProfileId);
      const match = discussions.find(
        (d) => d.linkedEntityId === refId && (!linkedEntityType || d.linkedEntityType === linkedEntityType),
      );
      discussionId = match?.id ?? null;
    } catch {
      // Leave null → fall back to the general community chat.
    }
  }

  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    if (!resolvedUserId) return 'unresolved'; // can't build ?viewedUserId= without it
    if (deleted) {
      window.location.assign(buildDiscussionUrl(resolvedUserId, DELETED_DISCUSSION_ROUTE));
      return 'deleted';
    }
    window.location.assign(buildDiscussionUrl(resolvedUserId, discussionId));
    return 'opened';
  }

  if (deleted) return 'deleted';
  if (!navigation) return 'unresolved';
  if (discussionId && resolvedProfileId != null) {
    navigation.navigate('EventThreadScreen', {
      channelId: `master-circle-${resolvedProfileId}`,
      parentMessageId: discussionId,
      masterProfileId: resolvedProfileId,
      masterName: masterName ?? undefined,
    });
    return 'opened';
  }
  if (resolvedUserId) {
    navigation.navigate('Dashboard', { viewedUserId: resolvedUserId, initialTab: 'communityChat' });
    return 'opened';
  }
  return 'unresolved';
}
